<template>
  <div class="view-container">
    <!-- 搜索区域 -->
    <div class="search-panel">
      <div class="search-row">
        <div class="search-item">
          <label><i class="fas fa-search"></i> 关键字:</label>
          <input v-model="searchKeyword" type="text" class="search-input" placeholder="搜索流程名称..." @keyup.enter="doSearch" autocomplete="off">
        </div>
        <div class="search-actions">
          <button @click="doSearch" class="btn btn-primary search-btn">
            <i class="fas fa-search"></i> 查询
          </button>
          <button @click="resetSearch" class="btn btn-default search-btn">
            <i class="fas fa-undo"></i> 重置
          </button>
        </div>
      </div>
    </div>

    <!-- 分类标签 -->
    <div class="category-tabs" v-if="categories.length > 1">
      <button
        v-for="cat in categories"
        :key="cat.id"
        class="category-tab"
        :class="{ active: activeCategory === cat.id }"
        @click="activeCategory = cat.id"
      >
        <span>{{ cat.name }}</span>
      </button>
    </div>

    <!-- 流程列表 -->
    <div class="process-grid" v-if="!loading">
      <template v-for="group in filteredGroups" :key="group.id">
        <div class="group-section">
          <div class="group-header" @click="toggleGroup(group.id)">
            <i class="fas" :class="collapsedGroups[group.id] ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
            <span>{{ group.title }}</span>
            <span class="group-count">{{ group.items?.length || 0 }}</span>
          </div>
          <div class="group-items" v-show="!collapsedGroups[group.id]">
            <div
              v-for="item in (group.items || [])"
              :key="item.id"
              class="process-card"
              @click="launchProcess(item)"
            >
              <div class="process-icon">
                <img v-if="getIconUrl(item)" :src="getIconUrl(item)" :alt="item.title" @error="onImgError($event)">
                <i v-else class="fas fa-cube default-process-icon"></i>
              </div>
              <div class="process-name">{{ item.title }}</div>
            </div>
          </div>
        </div>
      </template>
      <div v-if="filteredGroups.length === 0" class="empty-grid">
        <i class="fas fa-folder-open"></i>
        <span>暂无可用流程</span>
      </div>
    </div>
    <div v-else class="loading-wrap">
      <i class="fas fa-spinner fa-spin"></i>
      <span>加载中...</span>
    </div>
    
    <!-- 任务发起弹窗 -->
    <TaskLaunchModal
      :visible="launchModalVisible"
      :process-id="selectedProcess?.guid || selectedProcess?.Guid || ''"
      :process-name="selectedProcess?.title || selectedProcess?.Title || ''"
      :form-guid="selectedProcess?.formGuid || selectedProcess?.FormGuid || ''"
      :designer-type="selectedProcess?.formType || selectedProcess?.FormType || '2'"
      @close="handleLaunchClose"
      @success="handleLaunchSuccess"
    />
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { getWfsStartProcesses } from '../api/task'
import TaskLaunchModal from '../components/TaskLaunchModal.vue'

const searchKeyword = ref('')
const activeCategory = ref('all')
const groups = ref([])
const loading = ref(false)
const collapsedGroups = reactive({})

const categories = computed(() => {
  const cats = [{ id: 'all', name: '全部' }]
  groups.value.forEach(g => {
    cats.push({ id: g.id, name: g.title })
  })
  return cats
})

// 按分类过滤
const filteredGroups = computed(() => {
  if (activeCategory.value === 'all') return groups.value
  return groups.value.filter(g => g.id === activeCategory.value)
})

// 获取图标 URL，无图标时返回空字符串走 v-else 默认图标
const getIconUrl = (item) => {
  if (item.icon && item.icon !== 'default-process.png') {
    if (item.icon.startsWith('http')) return item.icon
    if (item.icon.startsWith('ChartInfo/') || item.icon.startsWith('/')) {
      return '/' + item.icon.replace(/^\//, '')
    }
  }
  if (item.logo) return '/' + item.logo.replace(/^\//, '')
  return ''
}

const onImgError = (e) => {
  e.target.style.display = 'none'
  const icon = document.createElement('i')
  icon.className = 'fas fa-cube default-process-icon'
  e.target.parentNode.appendChild(icon)
}

const toggleGroup = (id) => {
  collapsedGroups[id] = !collapsedGroups[id]
}

const doSearch = () => { loadData() }

const resetSearch = () => { 
  searchKeyword.value = '' 
  activeCategory.value = 'all'
  loadData() 
}

const loadData = async () => {
  loading.value = true
  try {
    const res = await getWfsStartProcesses(searchKeyword.value || '')
    const raw = res.groups || res.Groups || []
    // 后端返回大写字段名，统一转为小写
    const data = raw.map(g => ({
      id: g.Id || g.id,
      title: g.Title || g.title,
      picUrl: g.PicUrl || g.picUrl || '',
      items: (g.Items || g.items || []).map(item => ({
        id: item.Id || item.id || item.Guid || item.guid || '',
        guid: item.Guid || item.guid || item.Id || item.id || '',
        title: item.Title || item.title || '',
        icon: item.Icon || item.icon || '',
        logo: item.Logo || item.logo || '',
        formGuid: item.FormGuid || item.formGuid || '',
        formType: item.FormType || item.formType || '2'
      }))
    }))
    
    groups.value = data
    // 默认全部展开
    data.forEach(g => {
      if (collapsedGroups[g.id] === undefined) {
        collapsedGroups[g.id] = false
      }
    })
  } catch (e) {
    console.error('加载可发起流程失败', e)
    groups.value = []
  } finally {
    loading.value = false
  }
}

const launchModalVisible = ref(false)
const selectedProcess = ref(null)

const launchProcess = (item) => {
  console.log('[LaunchableProcesses] 选中的流程:', item)
  
  // 检查item中是否包含formGuid和formType
  const formGuid = item.formGuid || item.FormGuid || item.formId || item.FormId || ''
  const formType = item.formType || item.FormType || item.designerType || item.DesignerType || '2'
  
  console.log('[LaunchableProcesses] 提取的表单信息:', { formGuid, formType })
  
  selectedProcess.value = {
    ...item,
    formGuid: formGuid,
    FormGuid: formGuid,
    formType: formType,
    FormType: formType
  }
  
  launchModalVisible.value = true
}

const handleLaunchSuccess = () => {
  launchModalVisible.value = false
  selectedProcess.value = null
  // 可选：刷新待办列表
  window.dispatchEvent(new CustomEvent('refreshTodoList'))
}

const handleLaunchClose = () => {
  launchModalVisible.value = false
  selectedProcess.value = null
}

onMounted(async () => {
  await loadData()
  // 从快捷入口跳转过来的自动发起
  const autoLaunchId = sessionStorage.getItem('autoLaunchProcessId')
  if (autoLaunchId) {
    sessionStorage.removeItem('autoLaunchProcessId')
    // 在所有流程中查找匹配的项
    for (const group of groups.value) {
      const item = (group.items || []).find(i => (i.guid || i.Guid) === autoLaunchId)
      if (item) {
        launchProcess(item)
        break
      }
    }
  }
})
</script>

<style scoped>
.view-container {
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 150px);
  overflow: hidden;
}

/* 搜索区域 */
.search-panel {
  background: #fff;
  border: 1px solid #e0e4e8;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 15px;
  flex-shrink: 0;
  box-shadow: 0 1px 4px rgba(0,0,0,0.04);
}
.search-row {
  display: flex;
  gap: 16px;
  align-items: flex-end;
  flex-wrap: wrap;
}
.search-item {
  display: flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}
.search-item label {
  font-size: 13px;
  color: #6b7280;
  font-weight: 500;
}
.search-item label i {
  margin-right: 2px;
  color: #1ab394;
  font-size: 12px;
}
.search-item .form-control {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  outline: none;
  background: #fafbfc;
  width: 280px;
}
.search-item .form-control:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 3px rgba(26,179,148,0.12);
  background: #fff;
}
.search-input {
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  font-size: 13px;
  width: 280px;
  outline: none;
  transition: border-color 0.2s, box-shadow 0.2s;
  background: #fafbfc;
}
.search-input:focus {
  border-color: #1ab394;
  box-shadow: 0 0 0 3px rgba(26,179,148,0.12);
  background: #fff;
}
.search-actions {
  display: flex;
  gap: 10px;
  margin-left: auto;
}
.search-actions .btn {
  padding: 8px 18px;
  font-size: 13px;
  border-radius: 6px;
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  border: none;
  outline: none;
  box-shadow: none;
}
.search-actions .btn-primary {
  background-color: #1ab394;
  color: #fff;
}
.search-actions .btn-primary:hover {
  background-color: #18a689;
}
.search-actions .btn-default {
  background-color: #f3f3f4;
  color: #676a6c;
  border: 1px solid #e7eaec;
}
.search-actions .btn-default:hover {
  background-color: #e7eaec;
}
.search-btn {
  border: none;
  outline: none;
  box-shadow: none;
}

/* 分类标签 */
.category-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
  flex-wrap: wrap;
  flex-shrink: 0;
  padding: 0 4px;
}
.category-tab {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 6px 16px;
  background: #f3f4f6;
  border: 1px solid #e7eaec;
  border-radius: 6px;
  font-size: 13px;
  color: #676a6c;
  cursor: pointer;
  transition: all 0.2s ease;
}
.category-tab:hover {
  border-color: #1ab394;
  color: #1ab394;
}
.category-tab.active {
  background: #1ab394;
  border-color: #1ab394;
  color: #fff;
}

/* 流程网格 */
.process-grid {
  overflow-y: auto;
  flex: 1;
  padding-right: 4px;
}

.group-section {
  margin-bottom: 4px;
}

.group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background: #f8f9fa;
  border: 1px solid #e7eaec;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 600;
  color: #374151;
  user-select: none;
  transition: background 0.15s;
}
.group-header:hover {
  background: #eef0f2;
}
.group-header i {
  font-size: 12px;
  color: #9ca3af;
  width: 14px;
  text-align: center;
}
.group-count {
  margin-left: auto;
  background: #e5e7eb;
  color: #676a6c;
  font-size: 12px;
  font-weight: 500;
  padding: 2px 10px;
  border-radius: 10px;
}

.group-items {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  gap: 12px;
  padding: 14px 8px 8px 8px;
}

/* 流程卡片 */
.process-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 20px 12px 16px;
  background: #fff;
  border: 1px solid #e7eaec;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: center;
}
.process-card:hover {
  border-color: #1ab394;
  box-shadow: 0 4px 16px rgba(26,179,148,0.12);
  transform: translateY(-2px);
}

.process-icon {
  width: 64px;
  height: 64px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  overflow: hidden;
  background: #f3f4f6;
}
.process-icon img {
  width: 48px;
  height: 48px;
  object-fit: contain;
}
.default-process-icon {
  font-size: 32px;
  color: #1ab394;
}

.process-name {
  font-size: 13px;
  font-weight: 500;
  color: #374151;
  line-height: 1.4;
  word-break: break-all;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

/* 空状态 */
.empty-grid {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: #9ca3af;
  gap: 12px;
}
.empty-grid i {
  font-size: 48px;
}
.empty-grid span {
  font-size: 14px;
}

.loading-wrap {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  gap: 10px;
  color: #9ca3af;
  font-size: 14px;
}
.loading-wrap i {
  font-size: 20px;
}
</style>
